import { authenticate as xAuthenticate } from '@nab/x-auth';
import type { AuthConfig } from '@nab/x-auth';
import type { App, AppActions, AuthToken, AppLoaderConfig, XEventListener, XEventResponse, RemoveListenerFn, EventActionConfig } from '@nab/x-types';
import type { AppLoader } from '@nab/x-app-loaders';
import { runtimeEventBus } from '../services/runtimeEventBus';
import { loadApp, getAuthConfig } from '../core';
import { getAuthConfigForInstanceId } from '../registries/instanceRegistry';

export function defaultActionsProvider(app: App, instanceId: string): AppActions {
    return {
        navigate: (uri: string, data?: any): Promise<void> => {
            Object.assign(window.location, { href: uri });
            return Promise.resolve();
        },
        authenticate: <T extends AuthConfig = AuthConfig>(
            configOverridesOrForceRefresh?: Partial<T> | boolean,
            forceRefresh: boolean = false
        ): Promise<AuthToken<T>> => {
            let configOverrides: Partial<T> = {};
            const authConfig = getAuthConfigForInstanceId(instanceId);
            if (typeof configOverridesOrForceRefresh === 'boolean') {
                // eslint-disable-next-line no-param-reassign
                forceRefresh = configOverridesOrForceRefresh;
            } else {
                configOverrides = configOverridesOrForceRefresh || {};
            }
            const config = {
                ...(<T>getAuthConfig()),
                ...(<T>authConfig || {}),
                appMetaData: app,
                ...configOverrides
            };

            return xAuthenticate<T>(config, forceRefresh);
        },
        dispatchEvent: <DATA = any, RES = void>(eventNameOrConfig: string | EventActionConfig, data?: DATA): Promise<XEventResponse<RES>[]> => {
            return runtimeEventBus.publish<DATA, RES>(instanceId, eventNameOrConfig, data);
        },
        addEventListener: <DATA = any, RES = void>(
            eventNameOrConfig: string | EventActionConfig,
            listener: XEventListener<DATA, RES>
        ): RemoveListenerFn => {
            return runtimeEventBus.subscribe(instanceId, eventNameOrConfig, listener);
        },
        removeEventListener: (eventName: string, listener?: XEventListener): void => {
            return runtimeEventBus.unsubscribe(instanceId, eventName, listener);
        },
        embedMiniApp: <A extends App = App>(...args: [AppLoaderConfig<A>] | [A, HTMLElement]): Promise<AppLoader<A>> => {
            const appLoaderConfig: AppLoaderConfig<A> =
                args.length === 1
                    ? args[0]
                    : {
                          hostElement: args[1],
                          appMetaData: args[0]
                      };
            appLoaderConfig.appMetaData.parentContext = {
                ...appLoaderConfig.appMetaData.parentContext,
                appMetaData: {
                    id: app.id,
                    name: app.name,
                    route: app.route,
                    description: app.description
                }
            };

            // @ts-ignore
            return loadApp(appLoaderConfig); // eslint-disable-line
        }
    };
}
